import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { AlertCircle, Loader2, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { homePathFor } from "@/lib/permissions";

/* Phase 38 (2026-08-14) — Option E "Editorial Ocean Cover"
 * Single full-bleed navy canvas, dusk-ocean photo whisper, blurred moon-orb glow,
 * oversized wordmark left, minimalist serif sign-in right. Preserves login/auth flow. */

const NAVY = "#04122B";
const RED  = "#E63946";
const BG_IMG = "/logo/login_bg_dusk_ocean.jpg";

export default function LoginPage() {
  const { login, verifyMfa, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [focusField, setFocusField] = useState(null);
  const [hoverSubmit, setHoverSubmit] = useState(false);
  // Phase A (2026-08-20) — MFA step 2 state
  const [mfaStep, setMfaStep] = useState(null); // {pendingToken, email}
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);

  useEffect(() => {
    if (sessionStorage.getItem("st_session_expired")) {
      sessionStorage.removeItem("st_session_expired");
      toast.error("Your session expired — please sign in again.");
    }
  }, []);

  if (user) return <Navigate to={homePathFor(user)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await login(email.trim(), password);
      if (res && res.mfaRequired) {
        setMfaStep({ pendingToken: res.pendingToken, email: res.email });
        setBusy(false);
        return;
      }
      navigate(homePathFor(res));
    } catch (e2) {
      setErr(formatApiError(e2));
      setBusy(false);
    }
  };

  const onVerifyMfa = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await verifyMfa({
        pendingToken: mfaStep.pendingToken,
        otp: useRecovery ? undefined : otp.trim(),
        recoveryCode: useRecovery ? recoveryCode.trim() : undefined,
        rememberDevice,
      });
      navigate(homePathFor(u));
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const cancelMfa = () => {
    setMfaStep(null);
    setOtp("");
    setRecoveryCode("");
    setUseRecovery(false);
    setErr("");
  };

  const onForgot = (e) => {
    e.preventDefault();
    toast.info("Please contact your system administrator to reset your password.");
  };

  const inputStyleBase = {
    width: "100%",
    padding: "12px 0",
    background: "transparent",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    outline: "none",
    color: "#FFFFFF",
    fontSize: 16,
    lineHeight: 1.4,
    fontFamily: "'Inter', sans-serif",
    letterSpacing: "0.005em",
    WebkitAppearance: "none",
    MozAppearance: "none",
    caretColor: "#FFFFFF",
  };
  const bottomBorder = (field) => ({
    borderBottom: `1px solid ${focusField === field ? "#FFFFFF" : "rgba(255,255,255,0.35)"}`,
    transition: "border-color 300ms ease",
  });

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      style={{ backgroundColor: NAVY, fontFamily: "'Inter', sans-serif" }}
      data-testid="login-page"
    >
      {/* Layer 2 — dusk ocean photo whisper */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("${BG_IMG}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.55,
          zIndex: 0,
        }}
      />

      {/* Layer 3 — blurred moon-over-water orb (positioned behind logo) */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          left: "12%",
          top: "20%",
          width: 640,
          height: 640,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(31,79,163,0.55) 0%, rgba(31,79,163,0.15) 45%, rgba(31,79,163,0) 70%)",
          filter: "blur(40px)",
          zIndex: 1,
        }}
      />

      {/* Layer 4 — film-grain / noise overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.06  0 0 0 0 0.12  0 0 0 0 0.24  0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          backgroundSize: "160px 160px",
          mixBlendMode: "overlay",
          zIndex: 2,
        }}
      />

      {/* Layer 10 — content stack */}
      <div
        className="relative flex flex-col lg:flex-row items-stretch min-h-screen w-full"
        style={{ zIndex: 10 }}
      >
        {/* LEFT ZONE — branding */}
        <section
          className="flex flex-col justify-center w-full lg:w-[55%]"
          style={{ padding: "40px 24px" }}
          data-testid="login-brand-header"
        >
          <div className="mx-auto lg:mx-0 max-w-2xl" data-testid="login-brand-block">
            {/* Wordmark — real colors, no filter */}
            <img
              src="/logo/somturk_wordmark_clean.png"
              alt="SOM-TURK PLC"
              draggable={false}
              className="select-none block"
              style={{
                width: "auto",
                height: "22vh",
                maxHeight: 420,
              }}
              data-testid="login-brand-logo"
            />
            {/* Divider */}
            <div
              aria-hidden="true"
              className="mt-6"
              style={{ width: 88, height: 1, background: "rgba(255,255,255,0.55)" }}
              data-testid="login-brand-divider"
            />
            {/* Primary tagline */}
            <h1
              className="mt-6"
              style={{
                fontSize: 20,
                fontWeight: 500,
                letterSpacing: "0.3em",
                color: "#FFFFFF",
                textTransform: "uppercase",
                fontFamily: "'Inter', sans-serif",
                margin: 0,
              }}
              data-testid="login-brand-tagline"
            >
              Together in Blue
            </h1>
            {/* Editorial subline */}
            <p
              className="mt-3"
              style={{
                fontSize: 14,
                fontWeight: 300,
                fontStyle: "italic",
                color: "rgba(184,212,240,0.85)",
                letterSpacing: "0.02em",
                fontFamily: "'Playfair Display', 'Georgia', serif",
                margin: 0,
              }}
              data-testid="login-brand-subline"
            >
              The maritime finance platform of a new era.
            </p>
          </div>
        </section>

        {/* RIGHT ZONE — minimalist sign-in typography */}
        <section
          className="flex flex-col justify-center w-full lg:w-[45%]"
          style={{ padding: "40px 24px" }}
          data-testid="login-signin-panel"
        >
          <form
            onSubmit={mfaStep ? onVerifyMfa : onSubmit}
            className="mx-auto w-full"
            style={{ maxWidth: 400 }}
            data-testid={mfaStep ? "login-mfa-form" : "login-form"}
          >
            {/* Eyebrow */}
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.32em",
                color: RED,
                textTransform: "uppercase",
              }}
              data-testid="login-eyebrow"
            >
              {mfaStep ? "Two-factor challenge" : "Sign in"}
            </div>

            {/* Serif heading */}
            <h2
              className="mt-4"
              style={{
                fontFamily: "'Playfair Display', 'EB Garamond', 'Georgia', serif",
                fontSize: 28,
                fontWeight: 400,
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                color: "#FFFFFF",
                margin: 0,
              }}
              data-testid="login-heading"
            >
              {mfaStep ? "Confirm your identity." : "Access your bridge."}
            </h2>

            {/* Error message */}
            {err ? (
              <div
                className="mt-6 flex items-start gap-2"
                style={{
                  padding: "10px 12px",
                  border: "1px solid rgba(230,57,70,0.4)",
                  background: "rgba(230,57,70,0.08)",
                  color: "#FFDEE1",
                  fontSize: 13,
                }}
                data-testid="login-error"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            ) : null}

            {mfaStep ? (
              <>
                <p
                  className="mt-6"
                  style={{ color: "rgba(184,212,240,0.8)", fontSize: 13,
                           fontFamily: "'Inter', sans-serif" }}
                  data-testid="login-mfa-hint"
                >
                  Signed in as <b style={{ color: "#fff" }}>{mfaStep.email}</b>.
                  {useRecovery
                    ? " Enter one of your 10 single-use recovery codes."
                    : " Open your authenticator app and enter the 6-digit code."}
                </p>
                <div style={{ marginTop: 32 }}>
                  <label
                    htmlFor="mfa-input"
                    style={{
                      display: "block", fontSize: 11,
                      letterSpacing: "0.24em",
                      color: "rgba(184,212,240,0.7)",
                      textTransform: "uppercase", marginBottom: 8,
                    }}
                  >
                    {useRecovery ? "Recovery Code" : "Authenticator Code"}
                  </label>
                  <div style={bottomBorder("mfa")}>
                    {useRecovery ? (
                      <input
                        id="mfa-input"
                        type="text"
                        autoFocus
                        autoComplete="one-time-code"
                        placeholder="XXXXX-XXXXX"
                        value={recoveryCode}
                        onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                        onFocus={() => setFocusField("mfa")}
                        onBlur={() => setFocusField(null)}
                        style={inputStyleBase}
                        data-testid="login-recovery-input"
                      />
                    ) : (
                      <input
                        id="mfa-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        autoFocus
                        autoComplete="one-time-code"
                        placeholder="123456"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        onFocus={() => setFocusField("mfa")}
                        onBlur={() => setFocusField(null)}
                        style={{ ...inputStyleBase, letterSpacing: "0.4em",
                                 fontSize: 22 }}
                        data-testid="login-otp-input"
                      />
                    )}
                  </div>
                </div>

                <label
                  className="mt-6 flex items-center gap-2 select-none"
                  style={{ color: "rgba(184,212,240,0.8)", fontSize: 12 }}
                  data-testid="login-remember-device"
                >
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    style={{ accentColor: RED }}
                  />
                  <span>Trust this device for 30 days</span>
                </label>

                <button
                  type="submit"
                  disabled={busy || (!useRecovery && otp.length !== 6) ||
                             (useRecovery && recoveryCode.length < 6)}
                  onMouseEnter={() => setHoverSubmit(true)}
                  onMouseLeave={() => setHoverSubmit(false)}
                  style={{
                    display: "block", width: "100%", marginTop: 32,
                    padding: "14px 0", background: "transparent", border: "none",
                    borderBottom: `2px solid ${hoverSubmit ? "#FFFFFF" : RED}`,
                    color: "#FFFFFF", fontSize: 14, fontWeight: 500,
                    letterSpacing: "0.24em", textTransform: "uppercase",
                    textAlign: "left",
                    cursor: busy ? "wait" : "pointer",
                    opacity: (busy || (!useRecovery && otp.length !== 6) ||
                              (useRecovery && recoveryCode.length < 6)) ? 0.55 : 1,
                    transition: "border-color 500ms ease",
                  }}
                  data-testid="login-mfa-submit"
                >
                  {busy ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Verifying…
                    </span>
                  ) : (
                    <><ShieldCheck size={14} style={{ display: "inline",
                       marginRight: 8 }} />Verify &nbsp;→</>
                  )}
                </button>

                <div className="mt-6 flex items-center justify-between"
                     style={{ fontSize: 12 }}>
                  <button
                    type="button"
                    onClick={() => { setUseRecovery(v => !v); setOtp(""); setRecoveryCode(""); setErr(""); }}
                    className="forgot-link"
                    style={{
                      background: "transparent", border: "none",
                      color: "rgba(255,255,255,0.55)",
                      cursor: "pointer", padding: 0,
                      letterSpacing: "0.02em",
                    }}
                    data-testid="login-toggle-recovery"
                  >
                    <KeyRound size={12} style={{ display: "inline", marginRight: 4 }} />
                    {useRecovery ? "Use authenticator code instead"
                                  : "Use a recovery code instead"}
                  </button>
                  <button
                    type="button"
                    onClick={cancelMfa}
                    style={{
                      background: "transparent", border: "none",
                      color: "rgba(255,255,255,0.45)",
                      cursor: "pointer", padding: 0,
                    }}
                    data-testid="login-mfa-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
            {/* Email */}
            <div style={{ marginTop: 40 }}>
              <label
                htmlFor="email"
                style={{
                  display: "block",
                  fontSize: 11,
                  letterSpacing: "0.24em",
                  color: "rgba(184,212,240,0.7)",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Email address
              </label>
              <div style={bottomBorder("email")}>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="name@somturk.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusField("email")}
                  onBlur={() => setFocusField(null)}
                  style={inputStyleBase}
                  data-testid="login-email"
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginTop: 24 }}>
              <label
                htmlFor="password"
                style={{
                  display: "block",
                  fontSize: 11,
                  letterSpacing: "0.24em",
                  color: "rgba(184,212,240,0.7)",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Password
              </label>
              <div style={{ ...bottomBorder("password"), display: "flex", alignItems: "center" }}>
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  placeholder=""
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusField("password")}
                  onBlur={() => setFocusField(null)}
                  style={{ ...inputStyleBase, paddingRight: 32, flex: 1 }}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.55)",
                    padding: 0,
                    marginLeft: 8,
                    display: "flex",
                    alignItems: "center",
                  }}
                  tabIndex={-1}
                  data-testid="login-password-toggle"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit — text with red underline */}
            <button
              type="submit"
              disabled={busy}
              onMouseEnter={() => setHoverSubmit(true)}
              onMouseLeave={() => setHoverSubmit(false)}
              style={{
                display: "block",
                width: "100%",
                marginTop: 40,
                padding: "14px 0",
                background: "transparent",
                border: "none",
                borderBottom: `2px solid ${hoverSubmit ? "#FFFFFF" : RED}`,
                color: "#FFFFFF",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                textAlign: "left",
                cursor: busy ? "wait" : "pointer",
                transition: "border-color 500ms ease",
                fontFamily: "'Inter', sans-serif",
                opacity: busy ? 0.7 : 1,
              }}
              data-testid="login-submit"
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  Signing in…
                </span>
              ) : (
                <>Continue &nbsp;→</>
              )}
            </button>

            {/* Forgot password */}
            <a
              href="#forgot"
              onClick={onForgot}
              className="block mt-6 forgot-link"
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.45)",
                textDecoration: "none",
                letterSpacing: "0.02em",
                transition: "color 200ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#FFFFFF")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.45)")}
              data-testid="login-forgot"
            >
              Forgot password?
            </a>
              </>
            )}
          </form>
        </section>
      </div>

      {/* Bottom-left copyright */}
      <div
        className="absolute left-0 bottom-0"
        style={{
          padding: "24px 32px",
          fontSize: 11,
          color: "rgba(255,255,255,0.4)",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          zIndex: 20,
        }}
        data-testid="login-brand-footer"
      >
        © 2026 · SOM-TURK PLC · Fisheries &amp; Sea Trade
      </div>

      {/* Placeholder for legacy testid used by prior smoke tests */}
      <span className="sr-only" data-testid="login-footer">
        © 2026 SOM-TURK PLC. All rights reserved.
      </span>

      {/* Responsive tweaks — logo scale + right-zone padding */}
      <style>{`
        @media (min-width: 640px) {
          [data-testid="login-brand-logo"] { height: 30vh !important; }
          [data-testid="login-heading"] { font-size: 36px !important; }
          [data-testid="login-brand-tagline"] { font-size: 24px !important; }
          [data-testid="login-brand-subline"] { font-size: 16px !important; }
          [data-testid="login-brand-header"] { padding: 48px 32px !important; }
          [data-testid="login-signin-panel"] { padding: 48px 32px !important; }
        }
        @media (min-width: 1024px) {
          [data-testid="login-brand-logo"] { height: min(46vh, 420px) !important; }
          [data-testid="login-heading"] { font-size: 44px !important; }
          [data-testid="login-brand-tagline"] { font-size: 32px !important; }
          [data-testid="login-brand-subline"] { font-size: 18px !important; }
          [data-testid="login-brand-header"] { padding: 80px 64px !important; }
          [data-testid="login-signin-panel"] { padding: 80px 64px !important; }
        }
        [data-testid="login-email"]::placeholder,
        [data-testid="login-password"]::placeholder {
          color: rgba(255,255,255,0.35);
        }
        input[data-testid="login-email"],
        input[data-testid="login-password"] {
          background: transparent !important;
          background-color: transparent !important;
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
        }
        input[data-testid="login-email"]:-webkit-autofill,
        input[data-testid="login-password"]:-webkit-autofill,
        input[data-testid="login-email"]:-webkit-autofill:hover,
        input[data-testid="login-password"]:-webkit-autofill:hover,
        input[data-testid="login-email"]:-webkit-autofill:focus,
        input[data-testid="login-password"]:-webkit-autofill:focus,
        input[data-testid="login-email"]:-webkit-autofill:active,
        input[data-testid="login-password"]:-webkit-autofill:active {
          -webkit-text-fill-color: #FFFFFF !important;
          -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
          box-shadow: 0 0 0 1000px transparent inset !important;
          transition: background-color 5000s ease-in-out 0s !important;
          caret-color: #FFFFFF !important;
          background-color: transparent !important;
          background: transparent !important;
        }
      `}</style>
    </div>
  );
}
